import React from 'react';
import { View } from 'react-native';
import { ApprovalDetailField } from './ApprovalDetailField';
import type { ColorPalette } from '../constants/colors';
import type { AttendancePunchSource } from '../types/attendance';
import {
  displayCorrectionBreaks,
  displayRemovedCorrectionBreaks,
} from '../utils/attendanceCorrectionDraft';
import { attendanceCorrectionSubmittedHighlight } from '../utils/attendanceCorrectionSubmittedFields';
import { punchSourceFromApiRow } from '../utils/attendanceEventSource';
import {
  formatAppliedOn,
  formatPermissionDate,
  formatPermissionTime,
} from '../utils/dateTime';

function text(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function Field({
  label,
  value,
  palette,
  highlighted = false,
  expandable = false,
  source,
  treatAsManualEdit,
}: {
  label: string;
  value: string;
  palette: ColorPalette;
  highlighted?: boolean;
  expandable?: boolean;
  source?: AttendancePunchSource | null;
  treatAsManualEdit?: boolean;
}) {
  return (
    <ApprovalDetailField
      label={label}
      value={value}
      palette={palette}
      highlighted={highlighted}
      expandable={expandable}
      source={source}
      treatAsManualEdit={treatAsManualEdit}
      editedTime={value}
    />
  );
}

type Props = {
  requestDetails: Record<string, unknown> | null | undefined;
  submittedAt?: string | null;
  rejectReason?: string | null;
  cancellationReason?: string | null;
  showRejectReason?: boolean;
  showCancellationReason?: boolean;
  fallbackEditorName?: string | null;
  palette: ColorPalette;
};

export function AttendanceCorrectionDetailFields({
  requestDetails,
  submittedAt = null,
  rejectReason = null,
  cancellationReason = null,
  showRejectReason = false,
  showCancellationReason = false,
  fallbackEditorName = null,
  palette,
}: Props) {
  const attendanceDate = formatPermissionDate(
    text(requestDetails?.attendanceDate ?? requestDetails?.requestDate),
  );
  const checkInTime = formatPermissionTime(
    text(requestDetails?.checkInTime ?? requestDetails?.requestedCheckInTime),
  );
  // A request without a check-out keeps the recorded one; show that instead
  // of a blank that reads as "check-out removed".
  const checkOutTime = formatPermissionTime(
    text(requestDetails?.checkOutTime ?? requestDetails?.requestedCheckOutTime) ||
      text(requestDetails?.existingCheckOutTime),
  );
  const removedBreaks = displayRemovedCorrectionBreaks(
    requestDetails?.removedBreaks,
  );
  const breaks = displayCorrectionBreaks({
    breaks: requestDetails?.breaks,
    breakStartTime: text(
      requestDetails?.breakStartTime ?? requestDetails?.requestedBreakStartTime,
    ),
    breakEndTime: text(
      requestDetails?.breakEndTime ?? requestDetails?.requestedBreakEndTime,
    ),
  });
  const submitted = attendanceCorrectionSubmittedHighlight(requestDetails);
  const editorName =
    text(requestDetails?.editedByName) ||
    text(requestDetails?.requesterName) ||
    fallbackEditorName;
  const editedAt = text(requestDetails?.editedAt) || submittedAt;
  const editReason = text(requestDetails?.reason);
  const checkInSource = punchSourceFromApiRow(requestDetails ?? {}, 'checkIn');
  const checkOutSource = punchSourceFromApiRow(requestDetails ?? {}, 'checkOut');
  const reason = text(requestDetails?.reason) || 'Not available';
  const appliedOn = formatAppliedOn(
    text(requestDetails?.submittedAt) ||
      text(requestDetails?.appliedAt) ||
      submittedAt,
  );
  const storedRejectReason =
    rejectReason ||
    text(requestDetails?.rejectReason) ||
    text(requestDetails?.rejectionReason);
  const storedCancellationReason =
    cancellationReason ||
    text(requestDetails?.cancellationReason) ||
    text(requestDetails?.cancellation_reason) ||
    text(requestDetails?.cancelReason) ||
    text(requestDetails?.cancel_reason) ||
    text(requestDetails?.cancelledReason) ||
    text(requestDetails?.canceledReason);

  function withEditMeta(
    punch: AttendancePunchSource,
    submittedField: boolean,
  ): AttendancePunchSource {
    return {
      ...punch,
      editedByName: punch.editedByName ?? (submittedField ? editorName : null),
      editedAt: punch.editedAt ?? (submittedField ? editedAt : null),
      editReason: punch.editReason ?? (submittedField ? editReason : null),
    };
  }

  function breakPunchSource(
    row: unknown,
    side: 'start' | 'end',
    submittedField: boolean,
  ): AttendancePunchSource {
    const object =
      row && typeof row === 'object' && !Array.isArray(row)
        ? (row as Record<string, unknown>)
        : {};
    return withEditMeta(punchSourceFromApiRow(object, side), submittedField);
  }

  return (
    <>
      <Field label="Date" value={attendanceDate} palette={palette} />
      <Field
        label="Check-in"
        value={checkInTime}
        palette={palette}
        highlighted={submitted.checkIn}
        source={withEditMeta(checkInSource, submitted.checkIn)}
        treatAsManualEdit={submitted.checkIn}
      />
      {breaks.map((item, index) => {
        const prefix = breaks.length > 1 ? `Break ${index + 1}` : 'Break';
        const breakHighlight = submitted.breaks[index];
        const rawBreaks = Array.isArray(requestDetails?.breaks)
          ? requestDetails.breaks
          : [];
        return (
          <View key={`${item.startTime}-${item.endTime}-${index}`}>
            <Field
              label={`${prefix} start`}
              value={formatPermissionTime(item.startTime)}
              palette={palette}
              highlighted={Boolean(breakHighlight?.start)}
              source={breakPunchSource(
                rawBreaks[index],
                'start',
                Boolean(breakHighlight?.start),
              )}
              treatAsManualEdit={Boolean(breakHighlight?.start)}
            />
            <Field
              label={`${prefix} end`}
              value={formatPermissionTime(item.endTime)}
              palette={palette}
              highlighted={Boolean(breakHighlight?.end)}
              source={breakPunchSource(
                rawBreaks[index],
                'end',
                Boolean(breakHighlight?.end),
              )}
              treatAsManualEdit={Boolean(breakHighlight?.end)}
            />
          </View>
        );
      })}
      {removedBreaks.map((item, index) => (
        <Field
          key={`removed-${item.startTime}-${index}`}
          label={removedBreaks.length > 1 ? `Removed break ${index + 1}` : 'Removed break'}
          value={
            item.endTime
              ? `${formatPermissionTime(item.startTime)} – ${formatPermissionTime(item.endTime)}`
              : formatPermissionTime(item.startTime)
          }
          palette={palette}
          highlighted
        />
      ))}
      <Field
        label="Check-out"
        value={checkOutTime}
        palette={palette}
        highlighted={submitted.checkOut}
        source={withEditMeta(checkOutSource, submitted.checkOut)}
        treatAsManualEdit={submitted.checkOut}
      />
      <Field label="Reason" value={reason} palette={palette} />
      <Field label="Applied On" value={appliedOn} palette={palette} />
      {showRejectReason ? (
        <Field
          label="Reject Reason"
          value={storedRejectReason || 'Not available'}
          palette={palette}
          expandable
        />
      ) : null}
      {showCancellationReason ? (
        <Field
          label="Cancellation Reason"
          value={storedCancellationReason || 'Not available'}
          palette={palette}
          expandable
        />
      ) : null}
    </>
  );
}
